<template>
  <main class="min-h-screen bg-[#f7f5ef] text-stone-950">
    <section class="border-b border-stone-200 bg-white">
      <div class="mx-auto max-w-5xl px-5 py-14 md:px-8 md:py-20">
        <p class="text-xs font-black uppercase tracking-[0.22em] text-teal-700">How ClearFlow ratings work</p>
        <h1 class="mt-4 max-w-4xl text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl">
          A star average tells you whether people were happy. It does not tell you what the company is good at.
        </h1>
        <p class="mt-6 max-w-3xl text-xl leading-9 text-stone-700">
          We read across the available review record and look for details that repeat: showing up on time, clearing downspouts, working safely on taller homes, cleaning the ground afterward, and responding when rain is close. The pattern is more useful than one glowing review at the top of a page.
        </p>
      </div>
    </section>

    <section class="py-14">
      <div class="mx-auto grid max-w-6xl gap-8 px-5 md:px-8 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <p class="text-xs font-black uppercase tracking-[0.2em] text-teal-700">The familiar way</p>
          <h2 class="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Most of us read two reviews and make a decision.</h2>
          <div class="mt-5 space-y-5 text-lg leading-8 text-stone-700">
            <p>
              I understand why. A homeowner searches for a gutter cleaner, opens a few tabs, and sees a 4.9 beside one company and a 4.7 beside another. Then they read the first enthusiastic review and maybe the first terrible one. Five minutes later, they are supposed to feel informed.
            </p>
            <p>
              The problem is not that those reviews are useless. Google says its score is the average of published ratings, and Yelp uses recommendation software to identify reviews it considers useful and reliable. Both platforms are doing a different job well: collecting experiences and helping people discover local businesses.
            </p>
            <p>
              But a homeowner still has to translate that material into a hiring decision. Was the reviewer talking about gutter cleaning or window washing? Was the house one story or three? Did “great service” mean careful roof work, a low price, or a friendly phone call? The stars flatten all of that into one number.
            </p>
          </div>
        </div>

        <aside class="rounded-2xl border border-stone-300 bg-white p-6 shadow-sm sm:p-8">
          <p class="text-xs font-black uppercase tracking-[0.2em] text-amber-700">A simple example</p>
          <p class="mt-4 text-2xl font-black leading-tight">Two cleaners can both have 4.9 stars and still be right for different houses.</p>
          <div class="mt-6 grid gap-4 sm:grid-cols-2">
            <div class="rounded-xl bg-stone-100 p-5">
              <p class="font-black">Cleaner A</p>
              <p class="mt-2 text-sm leading-6 text-stone-700">Reviews repeatedly mention fast routine cleanouts, fair scheduling, and tidy work on one-story homes.</p>
            </div>
            <div class="rounded-xl bg-stone-100 p-5">
              <p class="font-black">Cleaner B</p>
              <p class="mt-2 text-sm leading-6 text-stone-700">Reviews repeatedly mention difficult access, second-story gutters, minor repairs, and before-and-after photos.</p>
            </div>
          </div>
          <p class="mt-5 leading-7 text-stone-700">The average is the same. The service pattern is not. That difference is where ClearFlow starts.</p>
        </aside>
      </div>
    </section>

    <section class="border-y border-stone-200 bg-white py-14">
      <div class="mx-auto max-w-6xl px-5 md:px-8">
        <div class="max-w-3xl">
          <p class="text-xs font-black uppercase tracking-[0.2em] text-teal-700">Our process</p>
          <h2 class="mt-3 text-3xl font-black tracking-tight sm:text-4xl">We turn a pile of opinions into a service profile.</h2>
          <p class="mt-4 text-lg leading-8 text-stone-700">
            Established companies may have hundreds of reviews across public sources. Newer specialists may have a few dozen. We use the evidence that actually exists and show the review count, because pretending every business has the same amount of history would make the comparison worse.
          </p>
        </div>

        <div class="mt-9 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          <article v-for="step in steps" :key="step.number" class="rounded-xl border border-stone-300 bg-[#f7f5ef] p-5">
            <p class="text-3xl font-black text-teal-800">{{ step.number }}</p>
            <h3 class="mt-4 text-xl font-black">{{ step.title }}</h3>
            <p class="mt-3 text-sm leading-6 text-stone-700">{{ step.copy }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="py-14">
      <div class="mx-auto max-w-6xl px-5 md:px-8">
        <p class="text-xs font-black uppercase tracking-[0.2em] text-teal-700">What changes for the homeowner</p>
        <h2 class="mt-3 max-w-3xl text-3xl font-black tracking-tight sm:text-4xl">You can compare the work, not only the reputation.</h2>

        <div class="mt-8 overflow-x-auto rounded-2xl border border-stone-300 bg-white shadow-sm">
          <div class="grid min-w-[720px] grid-cols-[0.85fr_1fr_1fr] bg-stone-950 px-4 py-4 text-xs font-black uppercase tracking-wide text-white sm:px-6">
            <span>Question</span><span>Generic review page</span><span>ClearFlow profile</span>
          </div>
          <div v-for="row in comparisons" :key="row.question" class="grid min-w-[720px] grid-cols-[0.85fr_1fr_1fr] gap-3 border-t border-stone-200 px-4 py-5 text-sm leading-6 sm:px-6">
            <strong>{{ row.question }}</strong><span class="text-stone-600">{{ row.generic }}</span><span class="font-semibold text-teal-900">{{ row.clearflow }}</span>
          </div>
        </div>
      </div>
    </section>

    <section class="border-y border-stone-200 bg-[#eef5f1] py-14">
      <div class="mx-auto grid max-w-6xl gap-8 px-5 md:px-8 lg:grid-cols-[1fr_0.8fr]">
        <div>
          <p class="text-xs font-black uppercase tracking-[0.2em] text-teal-700">What the score includes</p>
          <h2 class="mt-3 text-3xl font-black tracking-tight">A useful rating needs more than sentiment.</h2>
          <p class="mt-4 text-lg leading-8 text-stone-700">
            ClearFlow combines the public review signal with the things that matter during an actual service call: response, quality, reliability, verification, and fit for the requested work. We also show the pieces separately. A score should help explain a company, not hide it behind arithmetic.
          </p>
        </div>
        <dl class="grid gap-3 sm:grid-cols-2">
          <div v-for="signal in signals" :key="signal.term" class="rounded-xl border border-teal-200 bg-white p-4">
            <dt class="font-black">{{ signal.term }}</dt>
            <dd class="mt-2 text-sm leading-6 text-stone-600">{{ signal.definition }}</dd>
          </div>
        </dl>
      </div>
    </section>

    <section class="bg-white py-14">
      <div class="mx-auto grid max-w-6xl gap-10 px-5 md:px-8 lg:grid-cols-[0.75fr_1.25fr]">
        <div>
          <p class="text-xs font-black uppercase tracking-[0.2em] text-teal-700">Honest limits</p>
          <h2 class="mt-3 text-3xl font-black tracking-tight">Analysis helps. It does not turn reviews into certainty.</h2>
          <p class="mt-4 leading-7 text-stone-700">
            Reviews are still written by individual people, public information changes, and a strong history cannot promise how the next job will go. We treat the profile as a better starting point, then encourage homeowners to confirm scope, pricing, insurance, and availability directly.
          </p>
        </div>
        <div class="divide-y divide-stone-200 rounded-xl border border-stone-300 px-5">
          <details v-for="faq in faqs" :key="faq.question" class="py-5">
            <summary class="cursor-pointer font-black">{{ faq.question }}</summary>
            <p class="mt-3 leading-7 text-stone-700">{{ faq.answer }}</p>
          </details>
        </div>
      </div>
    </section>

    <section class="bg-teal-800 py-12 text-white">
      <div class="mx-auto flex max-w-6xl flex-col justify-between gap-6 px-5 md:flex-row md:items-center md:px-8">
        <div>
          <p class="text-xs font-black uppercase tracking-[0.2em] text-teal-100">See the difference</p>
          <h2 class="mt-3 max-w-3xl text-3xl font-black tracking-tight">Find the cleaner whose reviews describe the work your house actually needs.</h2>
        </div>
        <NuxtLink class="inline-flex shrink-0 justify-center rounded-md bg-white px-5 py-3 text-sm font-black uppercase tracking-wide text-teal-900" to="/cleaners">Compare cleaners</NuxtLink>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
const steps = [
  { number: "01", title: "Gather the record", copy: "We review the available public feedback and business information instead of choosing one testimonial to represent the company." },
  { number: "02", title: "Separate job types", copy: "A window-cleaning compliment should not become evidence of gutter expertise. We look for reviews tied to the relevant work." },
  { number: "03", title: "Find repeated details", copy: "We track themes such as response time, cleanup, difficult access, downspout work, photos, repairs, and recurring maintenance." },
  { number: "04", title: "Build a practical profile", copy: "The repeated signals become review themes, service-fit notes, rating components, and questions a homeowner should still ask." },
];

const comparisons = [
  { question: "Were customers happy?", generic: "A star average and individual comments.", clearflow: "Review average, volume, and confidence shown together." },
  { question: "Good for my job?", generic: "You read until you find a similar project.", clearflow: "Service types, property fit, access experience, and review themes." },
  { question: "Will they respond?", generic: "Response details may be scattered across reviews.", clearflow: "A visible response score and expected response window." },
  { question: "What do they do well?", generic: "Usually requires reading many reviews yourself.", clearflow: "Repeated strengths summarized with supporting case studies." },
];

const signals = [
  { term: "Review signal", definition: "Average rating, review volume, and the strength of repeated service themes." },
  { term: "Response", definition: "How consistently the available evidence points to timely replies and scheduling." },
  { term: "Quality", definition: "Thoroughness, finished condition, flow restoration, and attention to the property." },
  { term: "Reliability", definition: "Showing up, communicating changes, completing scope, and leaving the site clean." },
  { term: "Verification", definition: "The amount of current business, service, insurance, and contact information we can confirm." },
  { term: "Service fit", definition: "Whether the cleaner’s experience matches the city, property, access, and requested job." },
];

const faqs = [
  { question: "Does ClearFlow replace Google or Yelp?", answer: "No. Those platforms provide valuable source material and broad local discovery. ClearFlow adds a gutter-specific layer that organizes the evidence around the job a homeowner needs." },
  { question: "Do you analyze hundreds of reviews for every company?", answer: "We analyze as much relevant public feedback as is available. Established companies may have hundreds of reviews; newer businesses may have only a few dozen. We show review counts so homeowners can judge the depth of evidence." },
  { question: "Can a company pay for a better rating?", answer: "No. Paid visibility should never alter the review, quality, reliability, response, or verification signals. Any sponsored placement should be labeled separately." },
  { question: "How often are profiles updated?", answer: "Profiles display a verification date. Review counts, services, contact details, and availability can change, so homeowners should confirm current information directly before hiring." },
];

useHead({
  title: "Why ClearFlow Ratings Go Beyond Google and Yelp Reviews",
  meta: [{ name: "description", content: "Learn how ClearFlow analyzes public gutter-cleaning reviews for repeated service patterns, response, quality, reliability, verification, and job fit." }],
  script: [{
    type: "application/ld+json",
    innerHTML: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.question, acceptedAnswer: { "@type": "Answer", text: faq.answer } })),
    }),
  }],
});
</script>
